import React, { useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { getLogger } from 'utils/datadog-logger';

const logger = getLogger('third-party-script-boundary');

// Defined at module scope so its identity is stable across renders. react-error-boundary
// rejects `fallback={null}` (it requires a valid element), so rendering nothing has to go
// through fallbackRender.
const renderNothing = (): null => null;

type Props = {
  children: React.ReactNode;
  /** Identifies the wrapped subtree in logs, e.g. 'analytics'. */
  name: string;
  /**
   * Values that, when changed, remount the subtree and give it another chance to
   * run. Without this a single transient error disables the subtree for the rest
   * of the SPA session; pass the current route to recover on navigation.
   */
  resetKeys?: Array<unknown>;
};

/**
 * Isolates a subtree that loads or runs third-party/dispensary-supplied
 * scripts (analytics, tag managers, etc.) so an exception thrown by that
 * external code degrades silently instead of propagating up to the top-level
 * ErrorBoundary and replacing the entire page with the generic server-error
 * screen.
 */
export function ThirdPartyScriptBoundary({ children, name, resetKeys }: Props): JSX.Element {
  // Logging via onError rather than from the fallback: onError fires once per
  // caught error in componentDidCatch, whereas a fallback that logs in an effect
  // re-logs every time it remounts.
  const handleError = useCallback(
    (error: Error) => {
      logger.error(`Third-party script boundary caught an error in "${name}"`, { error: error.message, name });
    },
    [name]
  );

  return (
    <ErrorBoundary fallbackRender={renderNothing} onError={handleError} resetKeys={resetKeys}>
      {children}
    </ErrorBoundary>
  );
}
